import React, { useEffect, useRef, useState } from 'react';
import OrderDesign from '../OrderDesign';

/**
 * DecommissionOverlay - Animates a decommissioned unit flying out of the screen
 * Player units fly to bottom, opponent units fly to top
 */
export const DecommissionOverlay = ({ decommission, unit, gameState, userId }) => {
  const overlayRef = useRef(null);
  const [startPosition, setStartPosition] = useState(null);
  const [currentPosition, setCurrentPosition] = useState({ left: 0, top: 0 });
  const [opacity, setOpacity] = useState(1);
  const [animationComplete, setAnimationComplete] = useState(false);
  const animationStartTimeRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    if (!decommission || !unit || !gameState || !userId || animationComplete) return;

    // Find the slot element to get starting position
    const findSlotPosition = () => {
      const slotSelector = `[data-slot-zone="${decommission.zone}"][data-slot-index="${decommission.index}"]`;
      const slotElement = document.querySelector(slotSelector);
      
      if (slotElement) {
        const rect = slotElement.getBoundingClientRect();
        const position = {
          left: rect.left + window.scrollX,
          top: rect.top + window.scrollY,
          width: rect.width || 180,
          height: rect.height || 120
        };
        return position;
      }
      
      // Fallback: use approximate position
      console.warn(`⚠️ DecommissionOverlay: Slot element not found for ${decommission.zone}[${decommission.index}]`);
      return {
        left: window.innerWidth / 2 - 90,
        top: decommission.isPlayerUnit ? window.innerHeight - 200 : 100,
        width: 180,
        height: 120
      };
    };

    // Wait for DOM to update
    const rafId = requestAnimationFrame(() => {
      const timeoutId = setTimeout(() => {
        const slotPos = findSlotPosition();
        setStartPosition(slotPos);
        setCurrentPosition({ left: slotPos.left, top: slotPos.top });
      }, 50);

      return () => {
        clearTimeout(timeoutId);
        cancelAnimationFrame(rafId);
      };
    });
  }, [decommission, unit, gameState, userId, animationComplete]);

  // Animate unit flying out of screen
  useEffect(() => {
    if (!startPosition || animationComplete) return;

    const duration = 1500; // 1.5 seconds
    const startX = startPosition.left;
    const startY = startPosition.top;
    
    // Calculate end position: bottom of screen for player units, top for opponent units
    const endY = decommission.isPlayerUnit 
      ? window.innerHeight + 200 // Fly to bottom (below screen)
      : -200; // Fly to top (above screen)
    const endX = startX; // Keep same X position (fly straight up/down)
    
    const deltaY = endY - startY;
    animationStartTimeRef.current = Date.now();

    const animate = () => {
      const elapsed = Date.now() - animationStartTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out function (starts fast, slows down)
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const currentY = startY + (deltaY * easeOut);
      setCurrentPosition({ left: startX, top: currentY });

      // Fade out as it flies (opacity decreases from 1 to 0.3)
      const currentOpacity = 1 - (progress * 0.7); // Fade to 30% opacity
      setOpacity(currentOpacity);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete
        setAnimationComplete(true);
      }
    };

    // Start animation after brief delay
    const startTimeout = setTimeout(() => {
      animationFrameRef.current = requestAnimationFrame(animate);
    }, 100);

    return () => {
      clearTimeout(startTimeout);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [startPosition, decommission.isPlayerUnit, animationComplete]);

  // Don't render if animation is complete or missing data
  if (!decommission || !unit || !startPosition || animationComplete) {
    return null;
  }

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        left: `${currentPosition.left}px`,
        top: `${currentPosition.top}px`,
        width: `${startPosition.width}px`,
        height: `${startPosition.height}px`,
        zIndex: 100000,
        pointerEvents: 'none',
        transition: 'none',
        willChange: 'left, top, opacity',
        isolation: 'isolate',
        opacity: opacity
      }}
    >
      <OrderDesign
        order={unit}
        minimal={true}
        zoneName={decommission.zone}
        slotIndex={decommission.index}
        gameState={gameState}
      />
    </div>
  );
};
